import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, IdCard, RotateCcw, Ban, Trash2, Package, Boxes, ListChecks, AlertTriangle,
} from 'lucide-react';
import { PageHeader, Button } from '../ui-v2';
import { Modal, Input, Select, Badge, EmptyState, Tooltip } from '../ui-v2.5';
import { getIcon } from '../inventario/icons';
import {
  listPerfiles, getPerfil, crearPerfil, actualizarPerfil, desactivarPerfil,
  listNegocios, listPuestos, listRecursos, listAreas, eliminarPerfilPermanente,
  listEmpresasColaboradoras,
  listEmpresasContratantes,
} from './api';
import EliminarModal from './components/EliminarModal';
import IncorporacionesTabs from './IncorporacionesTabs';
import { activosDelAmbito, ambitoDe, nombreDelPadre, INTERNO, COLABORADOR } from './pertenencia';

/**
 * Perfiles — pantalla de administración (§3.5 de Fase 5), la más compleja. Tarjetas en rejilla con
 * dos indicadores (recursos / tareas), tituladas por el puesto; editor en Modal XL con tres secciones
 * (Identidad · Recursos · Tareas). Todo se guarda en UNA petición (el backend hace replace-all).
 *
 * Reglas de negocio en cliente:
 *  - Recursos: checkboxes agrupados por ámbito, filtrados por tipo de persona (no inventariables
 *    siempre; inventariables solo si su ámbito casa: interno↔interno, colaborador↔colab). Grupo vacío
 *    tras el filtro → línea explicativa, no se oculta.
 *  - Cambiar tipo de persona con recursos elegidos → avisa de los que quedan fuera de ámbito y pide
 *    confirmación; nunca borra en silencio.
 *  - Cambiar negocio → limpia el puesto y avisa.
 */

const SCOPE_DE_TIPO = { interno: 'interno', colaborador: 'colab' };
const TIPO_LABEL = { interno: 'Interno', colaborador: 'Colaborador' };
const TIPO_PLURAL = { interno: 'internos', colaborador: 'colaboradores' };
const cuerpoError = (e) => (e && e.response && e.response.data) || {};
/* El tipo de Inventario del recurso PARA el ámbito del perfil (15/10): un recurso tiene uno para
 * internos (`tipo_activo`) y otro para colaboradoras (`tipo_activo_colab`). Antes había uno solo,
 * siempre de internos, y un perfil de colaboradoras no podía llevar Coche ni Portátil. */
const tipoDeAmbito = (r, tipo) => (SCOPE_DE_TIPO[tipo] === 'colab' ? r.tipo_activo_colab : r.tipo_activo) || null;
const enAmbito = (r, tipo) => !r.inventariable || !!tipoDeAmbito(r, tipo);

export default function Perfiles() {
  const [perfiles, setPerfiles] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [colaboradoras, setColaboradoras] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null);
  const [editor, setEditor] = useState(null);       // { perfil|null }
  const [aDesactivar, setADesactivar] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      // Sin `listRoles`: el perfil ya no declara rol, asi que pedirlo era una llamada que no
      // alimentaba nada.
      /* El orden de esta lista ES el de la desestructuracion: meter una llamada en medio corre
       * todos los nombres de ahi para abajo. Al anadir las colaboradoras la puse en sexto lugar y
       * `em` paso a recibirlas, dejando a las empresas CONTRATANTES sin nombre — habria roto un
       * selector que nadie estaba tocando. Lo nuevo va al final. */
      const [pf, ns, ps, rc, ar, em, colabs] = await Promise.all([
        listPerfiles(), listNegocios(), listPuestos(), listRecursos(), listAreas(),
        listEmpresasContratantes(), listEmpresasColaboradoras(),
      ]);
      setPerfiles(pf);
      setNegocios(ns?.negocios || []);
      setPuestos(ps);
      setRecursos(rc);
      setAreas(ar);
      setEmpresas(em?.empresas || []);
      setColaboradoras(colabs || []);
    } catch (e) {
      console.error('[incorp][perfiles] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const recursosActivos = useMemo(() => recursos.filter((r) => r.activo), [recursos]);
  const areasActivas = useMemo(() => areas.filter((a) => a.activa), [areas]);

  async function reactivarDesdeCard(p) {
    // Reactivar requiere el detalle (recursos/tareas) para no vaciarlos en el replace-all.
    setAccionId(p.id);
    try {
      const d = await getPerfil(p.id);
      /* `negocio_id` ya no viaja: desde el 02/10 el padre del perfil lo pone su PUESTO, y
       * mandarlo era mandar una copia de algo que el servidor ya sabe. */
      await actualizarPerfil(p.id, {
        puesto_id: d.puesto_id, tipo_persona: d.tipo_persona,
        rol_sistema: d.rol_sistema, activo: true,
        recursos: d.recursos.map((r) => r.id),
        tareas: d.tareas.map((t) => ({ descripcion: t.descripcion, area_id: t.area_id, orden: t.orden })),
      });
      toast.success('Perfil reactivado.');
      await cargar();
    } catch (e) {
      console.error('[incorp][perfiles] reactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el perfil.');
    } finally {
      setAccionId(null);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Perfiles"
        subtitle="Plantillas de incorporación por puesto: recursos a preparar y tareas de onboarding."
        action={<Button onClick={() => setEditor({ perfil: null })}><Plus />Nuevo perfil</Button>}
      />

      <IncorporacionesTabs active="perfiles" />

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando perfiles…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar los perfiles.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : perfiles.length === 0 ? (
        <EmptyState
          variant="medium"
          icon={IdCard}
          title="Aún no hay perfiles"
          description="Crea el primer perfil de incorporación para un puesto."
          action={<Button onClick={() => setEditor({ perfil: null })}><Plus />Nuevo perfil</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {perfiles.map((p) => (
            <PerfilCard
              key={p.id}
              perfil={p}
              accion={accionId === p.id}
              onEditar={() => setEditor({ perfil: p })}
              onDesactivar={() => setADesactivar(p)}
              onReactivar={() => reactivarDesdeCard(p)}
              onEliminar={() => setAEliminar(p)}
            />
          ))}
        </div>
      )}

      {editor && (
        <PerfilModal
          perfil={editor.perfil}
          negocios={negocios}
          colaboradoras={colaboradoras}
          puestos={puestos}
          empresas={empresas}
          recursosActivos={recursosActivos}
          areasActivas={areasActivas}
          onClose={() => setEditor(null)}
          onGuardado={() => { setEditor(null); cargar(); }}
        />
      )}

      {aDesactivar && (
        <DesactivarPerfilModal
          perfil={aDesactivar}
          onClose={() => setADesactivar(null)}
          onDesactivado={() => { setADesactivar(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar perfil"
          subtitulo={aEliminar.puesto ? `${aEliminar.puesto.nombre} · ${TIPO_LABEL[aEliminar.tipo_persona] || aEliminar.tipo_persona}` : undefined}
          exitoMsg="Perfil eliminado permanentemente."
          eliminar={() => eliminarPerfilPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará este perfil de forma permanente. Se perderán sus{' '}
          <strong>{aEliminar.recursos_count} recurso(s)</strong> y{' '}
          <strong>{aEliminar.tareas_count} tarea(s)</strong> de onboarding asociados. Esta acción no se
          puede deshacer.
        </EliminarModal>
      )}
    </div>
  );
}

// ─────────────────────────── Tarjeta de perfil ───────────────────────────
function PerfilCard({ perfil, accion, onEditar, onDesactivar, onReactivar, onEliminar }) {
  const puestoNombre = perfil.puesto ? perfil.puesto.nombre : '(sin puesto)';
  // El padre puede ser un negocio o una empresa colaboradora; la tarjeta ensena el que sea.
  const negocioNombre = nombreDelPadre(perfil) || '—';
  return (
    <div className={`rounded-v25-lg border border-v25-line bg-white p-4 shadow-v25-btn ${perfil.activo ? '' : 'opacity-70'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-v25-ink-900">{puestoNombre}</h3>
          <p className="mt-0.5 truncate text-xs text-v25-ink-500">{negocioNombre} · {TIPO_LABEL[perfil.tipo_persona] || perfil.tipo_persona}</p>
        </div>
        {perfil.activo ? (
          null
        ) : <Badge tone="expired">Desactivado</Badge>}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-v25-full bg-v25-surface-sunken px-2 py-0.5 text-xs text-v25-ink-500">
          <Package className="h-3.5 w-3.5" /> {perfil.recursos_count} recursos
        </span>
        <span className="inline-flex items-center gap-1 rounded-v25-full bg-v25-surface-sunken px-2 py-0.5 text-xs text-v25-ink-500">
          <ListChecks className="h-3.5 w-3.5" /> {perfil.tareas_count} tareas
        </span>
      </div>

      {/* Acciones — solo icono (el texto se salía de la tarjeta); mismos iconos que las tablas de
          Recursos/Puestos + title. Eliminar (permanente) separada y en rojo para no confundirla. */}
      <div className="mt-4 flex items-center justify-end gap-1 border-t border-v25-line pt-3">
        <Tooltip content="Editar"><Button variant="ghost" size="icon" onClick={onEditar}><Pencil /></Button></Tooltip>
        {perfil.activo ? (
          <Tooltip content="Desactivar"><Button variant="ghost" size="icon" onClick={onDesactivar}><Ban /></Button></Tooltip>
        ) : (
          <Tooltip content="Reactivar">
            <Button variant="ghost" size="icon" disabled={accion} onClick={onReactivar}>
              {accion ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            </Button>
          </Tooltip>
        )}
        <span className="mx-1 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
        <Tooltip content="Eliminar permanentemente">
          <Button variant="ghost" size="icon" onClick={onEliminar} className="text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

// ─────────────────────────── Modal editor (crear/editar) ───────────────────────────
function PerfilModal({ perfil, negocios, colaboradoras, puestos, empresas, recursosActivos, areasActivas, onClose, onGuardado }) {
  const esEdicion = !!(perfil && perfil.id);
  const [cargandoDetalle, setCargandoDetalle] = useState(esEdicion);
  const [negocioId, setNegocioId] = useState('');
  const [colaboradoraId, setColaboradoraId] = useState('');
  const [puestoId, setPuestoId] = useState('');
  const [tipoPersona, setTipoPersona] = useState('interno');
  const [empresaContratanteId, setEmpresaContratanteId] = useState('');
  const [recursosSel, setRecursosSel] = useState(new Set());
  const [tareas, setTareas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [reactivable, setReactivable] = useState(null);          // { id } del 409 tripleta
  const [confirmTipo, setConfirmTipo] = useState(null);          // { nuevo, afectados[] }
  const keyRef = useRef(0);
  const uid = () => { keyRef.current += 1; return `t${keyRef.current}`; };

  // Carga del detalle al editar (recursos + tareas).
  useEffect(() => {
    if (!esEdicion) return undefined;
    let cancel = false;
    getPerfil(perfil.id)
      .then((d) => {
        if (cancel) return;
        setNegocioId(d.negocio_id ? String(d.negocio_id) : '');
        setColaboradoraId(d.empresa_colaboradora_id ? String(d.empresa_colaboradora_id) : '');
        setPuestoId(String(d.puesto_id));
        setTipoPersona(d.tipo_persona);
        setEmpresaContratanteId(d.empresa_contratante_id ? String(d.empresa_contratante_id) : '');
        setRecursosSel(new Set(d.recursos.map((r) => r.id)));
        setTareas(d.tareas.map((t) => ({ _key: uid(), descripcion: t.descripcion, area_id: t.area_id, orden: t.orden })));
      })
      .catch(() => { if (!cancel) toast.error('No se pudo cargar el perfil.'); })
      .finally(() => { if (!cancel) setCargandoDetalle(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esEdicion, perfil]);

  /* El perfil hereda el padre de su PUESTO, asi que lo primero es acotar de que puestos se puede
   * elegir: los del negocio si es de interno, los de la empresa colaboradora si es de colaborador.
   * El filtro de antes miraba solo `negocio_id`, y un puesto de colaborador lo tiene a NULL: no
   * habria salido ninguno, sin error y sin pista. */
  const ambito = useMemo(
    () => ambitoDe(tipoPersona, { negocioId, colaboradoraId }),
    [tipoPersona, negocioId, colaboradoraId],
  );
  const esColab = tipoPersona === COLABORADOR;
  const padreElegido = esColab ? !!colaboradoraId : !!negocioId;
  const puestosDelAmbito = useMemo(() => activosDelAmbito(puestos, ambito), [puestos, ambito]);
  // Tres grupos, no dos. Una credencial no es "material no inventariable": tiene otro ciclo
  // —la crea Sistemas y la revoca la baja— y desde la migración va a otra tabla. Mezclarlas en el
  // mismo bloque escondía esa diferencia justo donde se decide qué lleva cada puesto.
  const credenciales = useMemo(
    () => recursosActivos.filter((r) => r.tipo === 'credencial'),
    [recursosActivos],
  );
  const noInventariables = useMemo(
    () => recursosActivos.filter((r) => r.tipo !== 'credencial' && !r.inventariable),
    [recursosActivos],
  );
  const inventariablesEnAmbito = useMemo(
    () => recursosActivos.filter((r) => r.tipo !== 'credencial' && r.inventariable && enAmbito(r, tipoPersona))
      // El grupo enseña el nombre del tipo DE ESTE ámbito, no siempre el de internos.
      .map((r) => ({ ...r, tipo_activo: tipoDeAmbito(r, tipoPersona) })),
    [recursosActivos, tipoPersona],
  );

  // Cambiar de padre → limpiar puesto y avisar. El puesto de antes ya no pertenece a nada.
  function cambiarPadre(nuevo, clase) {
    if (clase === COLABORADOR) setColaboradoraId(nuevo); else setNegocioId(nuevo);
    if (puestoId) {
      setPuestoId('');
      toast.info(clase === COLABORADOR
        ? 'Puesto restablecido por el cambio de empresa colaboradora: elige uno de la nueva.'
        : 'Puesto restablecido por el cambio de negocio: elige uno del nuevo negocio.');
    }
  }

  // Cambiar tipo de persona → si hay recursos elegidos fuera de ámbito, confirmar antes de quitarlos.
  function cambiarTipo(nuevo) {
    if (nuevo === tipoPersona) return;
    const afectados = recursosActivos.filter((r) => recursosSel.has(r.id) && r.inventariable && !enAmbito(r, nuevo));
    if (afectados.length === 0) { aplicarTipo(nuevo); return; }
    setConfirmTipo({ nuevo, afectados });
  }
  function confirmarCambioTipo() {
    const { nuevo, afectados } = confirmTipo;
    const quitar = new Set(afectados.map((r) => r.id));
    setRecursosSel((prev) => new Set([...prev].filter((id) => !quitar.has(id))));
    aplicarTipo(nuevo);
    setConfirmTipo(null);
  }

  /* Cambiar el tipo cambia DE QUE CUELGA el perfil, asi que el puesto elegido deja de valer: un
   * puesto de interno no puede sostener un perfil de colaborador, y el servidor lo rechaza con
   * `perfil_tipo_no_casa`. Dejarlo puesto seria ofrecer un formulario que ya no se puede guardar. */
  function aplicarTipo(nuevo) {
    setTipoPersona(nuevo);
    if (puestoId) setPuestoId('');
  }

  const toggleRecurso = (id) => setRecursosSel((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });

  // Tareas
  const tareasOrdenadas = useMemo(() => [...tareas].sort((a, b) => a.orden - b.orden), [tareas]);
  const addTarea = () => {
    const maxOrden = tareas.reduce((m, t) => Math.max(m, t.orden), -1);
    setTareas((prev) => [...prev, { _key: uid(), descripcion: '', area_id: null, orden: maxOrden + 1 }]);
  };
  const updTarea = (key, campo, valor) => setTareas((prev) => prev.map((t) => (t._key === key ? { ...t, [campo]: valor } : t)));
  const delTarea = (key) => setTareas((prev) => prev.filter((t) => t._key !== key));

  function validar() {
    const err = {};
    if (!padreElegido) err.padre = esColab ? 'Selecciona una empresa colaboradora.' : 'Selecciona un negocio.';
    if (!puestoId) err.puesto = 'Selecciona un puesto.';
    if (!tipoPersona) err.tipo = 'Selecciona el tipo de persona.';
    if (tareas.some((t) => !t.descripcion.trim())) err.tareas = 'Hay tareas sin descripción.';
    setErrores(err);
    if (Object.keys(err).length) return null;
    /* Ni `negocio_id` ni `empresa_colaboradora_id` viajan: el servidor los lee del puesto. Lo que
     * aqui se elige sirve para ACOTAR la lista de puestos, no para guardarse. */
    return {
      puesto_id: Number(puestoId),
      tipo_persona: tipoPersona,
      empresa_contratante_id: empresaContratanteId ? Number(empresaContratanteId) : null,
      recursos: [...recursosSel],
      tareas: tareas.map((t) => ({ descripcion: t.descripcion.trim(), area_id: t.area_id || null, orden: t.orden })),
    };
  }

  async function guardar() {
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    setReactivable(null);
    try {
      if (esEdicion) {
        await actualizarPerfil(perfil.id, payload);
        toast.success('Perfil actualizado.');
      } else {
        await crearPerfil(payload);
        toast.success('Perfil creado.');
      }
      onGuardado();
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'perfil_duplicado_inactivo' && body.id) {
        setReactivable({ id: body.id });
      } else if (body.error === 'perfil_duplicado') {
        toast.error('Ya existe un perfil para este puesto.');
      } else if (body.error === 'ambito_incoherente') {
        toast.error(body.message || 'Hay recursos cuyo ámbito no corresponde al tipo de persona.');
      } else {
        console.error('[incorp][perfiles] guardar', e);
        toast.error(body.message || 'No se pudo guardar el perfil.');
      }
    } finally {
      setGuardando(false);
    }
  }

  async function reactivarDuplicado() {
    if (!reactivable) return;
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    try {
      await actualizarPerfil(reactivable.id, { ...payload, activo: true });
      toast.success('Perfil reactivado con estos valores.');
      onGuardado();
    } catch (e) {
      console.error('[incorp][perfiles] reactivarDuplicado', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el perfil.');
    } finally {
      setGuardando(false);
    }
  }

  const footer = reactivable ? (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={reactivarDuplicado} disabled={guardando}>
        {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar el perfil existente
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={guardar} disabled={guardando || cargandoDetalle}>
        {guardando ? <Loader2 className="animate-spin" /> : null}{esEdicion ? 'Guardar cambios' : 'Crear perfil'}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={esEdicion ? 'Editar perfil' : 'Nuevo perfil'}
      subtitle={esEdicion && perfil.puesto ? `${perfil.puesto.nombre} · ${nombreDelPadre(perfil)}` : 'Define identidad, recursos y tareas.'}
      footer={footer}
    >
      {cargandoDetalle ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando perfil…
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── Sección Identidad ── */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Identidad</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* EL ORDEN IMPORTA: el tipo decide de que cuelga el perfil, el padre decide que
                  puestos existen, y el puesto decide todo lo demas. Puesto al reves -como estaba-
                  se elige un puesto antes de saber de donde puede salir. */}
              <Select label="Tipo de persona *" value={tipoPersona} onChange={(e) => cambiarTipo(e.target.value)} error={errores.tipo}
                hint={esColab
                  ? 'Cuelga de una empresa colaboradora, no de una marca.'
                  : 'Cuelga de una de las marcas del grupo.'}>
                <option value={INTERNO}>Interno</option>
                <option value={COLABORADOR}>Colaborador</option>
              </Select>
              {esColab ? (
                <Select label="Empresa colaboradora *" value={colaboradoraId} onChange={(e) => cambiarPadre(e.target.value, COLABORADOR)} error={errores.padre}
                  hint={colaboradoras.length === 0 ? 'No hay empresas colaboradoras dadas de alta.' : undefined}>
                  <option value="">Selecciona una empresa…</option>
                  {colaboradoras.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </Select>
              ) : (
                <Select label="Negocio *" value={negocioId} onChange={(e) => cambiarPadre(e.target.value, INTERNO)} error={errores.padre}>
                  <option value="">Selecciona un negocio…</option>
                  {negocios.map((n) => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                </Select>
              )}
              <Select
                label="Puesto *"
                value={puestoId}
                onChange={(e) => setPuestoId(e.target.value)}
                error={errores.puesto}
                disabled={!padreElegido}
                hint={!padreElegido
                  ? (esColab ? 'Elige antes la empresa colaboradora.' : 'Elige antes un negocio.')
                  : (puestosDelAmbito.length === 0
                    ? (esColab
                      ? 'Esta empresa colaboradora no tiene puestos activos. Créalos en Puestos.'
                      : 'Este negocio no tiene puestos activos.')
                    : undefined)}
              >
                <option value="">Selecciona un puesto…</option>
                {puestosDelAmbito.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </Select>
              {/* EL ROL DEL SISTEMA YA NO SE DECLARA AQUI (Fase C): todas las altas nacen como
                  «General» y lo decide el SERVIDOR, asi que declararlo en el perfil no cambiaba
                  nada y sugeria que si. La columna `incorp_perfiles.rol_sistema` se queda —ver la
                  nota en `IncorpPerfil.js`—, pero ya nadie la escribe ni la lee. */}
              {/* LA RAZON SOCIAL QUE ESTE PERFIL DECLARA.
                  Opcional, igual que el rol: si se declara, el alta la trae puesta y se puede
                  cambiar alli; si no, el alta la pide en blanco.
                  DECLARA y no deduce: la razon social NO se infiere del negocio —34 personas
                  trabajan para dos o mas marcas con una sola razon social—, asi que alguien tiene
                  que decir cual es, y ese alguien es este perfil. */}
              <Select label="Empresa contratante" value={empresaContratanteId}
                onChange={(e) => setEmpresaContratanteId(e.target.value)}
                hint="Opcional. Si se declara, el alta la trae prellenada.">
                <option value="">— Sin declarar —</option>
                {empresas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
              </Select>
            </div>

            {/* Aviso de cambio de tipo con recursos fuera de ámbito */}
            {confirmTipo && (
              <div className="rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-xs text-v25-expired-fg">
                <div className="mb-1.5 flex items-center gap-1.5 font-medium">
                  <AlertTriangle className="h-4 w-4" /> Al cambiar a «{TIPO_LABEL[confirmTipo.nuevo]}» estos recursos quedan fuera de ámbito y se quitarán:
                </div>
                <ul className="mb-2 list-disc pl-5">
                  {confirmTipo.afectados.map((r) => <li key={r.id}>{r.nombre}</li>)}
                </ul>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmTipo(null)}>Cancelar</Button>
                  <Button variant="destructive" size="sm" onClick={confirmarCambioTipo}>Quitarlos y cambiar</Button>
                </div>
              </div>
            )}
          </section>

          {/* ── Sección Recursos ── */}
          <section className="space-y-2 border-t border-v25-line pt-4">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
              <Boxes className="h-3.5 w-3.5" /> Recursos
            </h4>

            <GrupoRecursos titulo="Credenciales" recursos={credenciales} sel={recursosSel} onToggle={toggleRecurso}
              vacio="No hay credenciales en el catálogo."
              nota="Cuentas y accesos. Al crear el alta nacen pendientes en la ficha de la persona, no como material." />
            <GrupoRecursos titulo="Material · no inventariable" recursos={noInventariables} sel={recursosSel} onToggle={toggleRecurso}
              vacio="No hay recursos no inventariables disponibles." />
            <GrupoRecursos titulo={`Material · inventariable · ${TIPO_LABEL[tipoPersona]}`} recursos={inventariablesEnAmbito} sel={recursosSel} onToggle={toggleRecurso}
              vacio={`Sin recursos inventariables disponibles para ${TIPO_PLURAL[tipoPersona]}.`} />
          </section>

          {/* ── Sección Tareas ── */}
          <section className="space-y-2 border-t border-v25-line pt-4">
            <div className="flex items-center justify-between">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
                <ListChecks className="h-3.5 w-3.5" /> Tareas de onboarding
              </h4>
              <Button variant="outline" size="sm" onClick={addTarea}><Plus />Añadir tarea</Button>
            </div>
            {errores.tareas && <p className="text-[11px] text-red-600">{errores.tareas}</p>}
            {tareasOrdenadas.length === 0 ? (
              <p className="text-xs text-v25-ink-400">Sin tareas. Añade las que deba completar cada incorporación de este perfil.</p>
            ) : (
              <ul className="space-y-2">
                {tareasOrdenadas.map((t) => (
                  <li key={t._key} className="flex items-start gap-2 rounded-v25-md border border-v25-line p-2">
                    <Input type="number" min={0} value={t.orden} onChange={(e) => updTarea(t._key, 'orden', Number(e.target.value))}
                      wrapperClassName="w-[64px]" aria-label="Orden" />
                    <Input value={t.descripcion} onChange={(e) => updTarea(t._key, 'descripcion', e.target.value)}
                      placeholder="Descripción de la tarea" maxLength={200} wrapperClassName="flex-1" />
                    <Select value={t.area_id == null ? '' : String(t.area_id)} onChange={(e) => updTarea(t._key, 'area_id', e.target.value ? Number(e.target.value) : null)}
                      wrapperClassName="w-[190px]" aria-label="Área responsable">
                      <option value="">Responsable directo</option>
                      {areasActivas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                    </Select>
                    <button type="button" onClick={() => delTarea(t._key)} className="mt-2 shrink-0 text-v25-ink-400 hover:text-red-600" aria-label="Eliminar tarea">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {reactivable && (
            <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
              <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Ya existe un perfil <strong>desactivado</strong> para este puesto, negocio y tipo de persona. En vez de
                crear otro, puedes reactivarlo con los valores de este formulario.
              </span>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// Grupo de checkboxes de recursos; si queda vacío tras el filtro, muestra la línea explicativa.
function GrupoRecursos({ titulo, recursos, sel, onToggle, vacio, nota }) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-medium text-v25-ink-500">{titulo}</p>
      {nota && <p className="mb-1.5 text-[11px] text-v25-ink-400">{nota}</p>}
      {recursos.length === 0 ? (
        <p className="rounded-v25-md border border-dashed border-v25-line px-3 py-2 text-xs text-v25-ink-400">{vacio}</p>
      ) : (
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {recursos.map((r) => {
            const Icon = getIcon(r.icono);
            const checked = sel.has(r.id);
            return (
              <label key={r.id} className="flex cursor-pointer items-center gap-2 rounded-v25-md border border-v25-line px-2.5 py-1.5 hover:bg-v25-surface-sunken">
                <input type="checkbox" checked={checked} onChange={() => onToggle(r.id)} className="h-4 w-4" />
                <Icon className="h-4 w-4 shrink-0 text-v25-ink-500" />
                <span className="truncate text-sm text-v25-ink-800">{r.nombre}</span>
                {r.tipo_activo && <span className="ml-auto shrink-0 text-[10px] text-v25-ink-400">{r.tipo_activo.nombre}</span>}
                {r.tipo === 'credencial' && r.campo_persona && (
                  <span className="ml-auto shrink-0 text-[10px] text-v25-ink-400">{r.campo_persona}</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Modal danger (desactivar) ───────────────────────────
function DesactivarPerfilModal({ perfil, onClose, onDesactivado }) {
  const [procesando, setProcesando] = useState(false);
  async function confirmar() {
    setProcesando(true);
    try {
      await desactivarPerfil(perfil.id);
      toast.success('Perfil desactivado.');
      onDesactivado();
    } catch (e) {
      console.error('[incorp][perfiles] desactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo desactivar el perfil.');
    } finally {
      setProcesando(false);
    }
  }
  const puesto = perfil.puesto ? perfil.puesto.nombre : 'este perfil';
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      tone="danger"
      title="Desactivar perfil"
      subtitle={perfil.puesto ? `${perfil.puesto.nombre} · ${TIPO_LABEL[perfil.tipo_persona] || perfil.tipo_persona}` : undefined}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
          <Button variant="destructive" onClick={confirmar} disabled={procesando}>
            {procesando ? <Loader2 className="animate-spin" /> : <Ban />}Desactivar
          </Button>
        </>
      )}
    >
      <p className="text-sm text-v25-ink-700">
        El perfil dejará de estar disponible para nuevas incorporaciones. Podrás reactivarlo después.
        ¿Seguro que quieres desactivar <strong>«{puesto}»</strong>?
      </p>
    </Modal>
  );
}
